import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Share2 } from 'lucide-react'
import { Button } from './Button'
import { useShare } from '../../hooks/use-share'
import { useSession } from '../../store/user.store'

interface Props {
  /** URL to share. When omitted, the user's referral deeplink is used —
   *  that way every casual share automatically becomes a tracked invite. */
  url?: string
  /** Optional caption / message attached to the share. */
  text?: string
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  /** Override the button label; defaults to t('actions.share'). */
  label?: string
  className?: string
  /**
   * Optional file fetcher. When provided we attach the file (e.g. natal PDF)
   * to the share via Web Share API — opens the OS share sheet so the user
   * can send the PDF directly to Telegram / WhatsApp / Mail / etc. Falls
   * back to text-only share if the platform doesn't support file sharing.
   */
  getFile?: () => Promise<File>
}

/**
 * Share button used everywhere we want users to send a link out (daily card,
 * natal full, compat invite, etc). Wraps the Button + useShare hook with the
 * familiar paper-plane icon, so the call sites just drop in `<ShareButton/>`
 * and don't reinvent the styling on each screen.
 */
export function ShareButton({
  url,
  text,
  variant = 'secondary',
  size = 'md',
  label,
  className,
  getFile,
}: Props) {
  const { t } = useTranslation()
  const share = useShare()
  const referralLink = useSession((s) => s.referralLink)
  const [busy, setBusy] = useState(false)

  async function handleClick() {
    setBusy(true)
    let files: File[] | undefined
    try {
      if (getFile) {
        try {
          files = [await getFile()]
        } catch {
          // PDF fetch failed — degrade to text-only share rather than blocking.
          files = undefined
        }
      }
      // Priority: explicit url (compat invite, etc) > referral deeplink > web origin.
      // The referral deeplink is the most valuable default — every casual
      // share doubles as an invite track.
      const shareUrl = url ?? referralLink ?? window.location.origin
      await share({ url: shareUrl, text: text ?? '', files })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      loading={busy}
      leftIcon={<Share2 className="h-4 w-4" strokeWidth={1.8} aria-hidden />}
      onClick={handleClick}
    >
      {label ?? t('actions.share')}
    </Button>
  )
}
